import { request } from '@core/api';
import { query } from '@core/redux/query';
import { Photo } from '@photos/types';
import { OrderNumber } from '@projects/types/orderNumbers';
import { errorBox } from '@core/components/AlertBox';
import { useCallback } from 'react';
import type { Scan } from '@scans/types';

export const useSMANumbers = () => {
  const fetchSMANumbers = useCallback(
    async (projectId: string, assetId: string, type: 'photos' | 'scans') =>
      await request<OrderNumber[]>('GET', {
        path: ['projects', projectId, type, assetId, 'orderNumbers'],
      }),
    [],
  );

  return { fetchSMANumbers };
};

const smaApi = query.injectEndpoints({
  endpoints: (builder) => ({
    // photo list endpoint does not provide orderNumbers,
    // so we need to fetch them on demand for selected photos
    updatePhotosWithSma: builder.query({
      queryFn: async ({ projectId, photos }: { projectId?: string; photos: Photo[] }) => {
        if (!projectId) {
          return { data: photos };
        }

        const smaNumbersResults = await Promise.allSettled(
          photos.map((photo) => {
            return request<OrderNumber[]>('GET', {
              path: ['projects', projectId, 'photos', photo.id, 'orderNumbers'],
            });
          }),
        );

        const updatedPhotos = [...photos];

        const errorPhotoIds = [] as string[];
        smaNumbersResults.forEach((result, index) => {
          if (result.status === 'rejected') {
            errorPhotoIds.push(photos[index].id);
          }
          if (result.status === 'fulfilled') {
            updatedPhotos[index] = { ...updatedPhotos[index], orderNumbers: result.value };
          }
        });

        if (errorPhotoIds.length > 0) {
          errorBox(`Failed to load SMA numbers for photos ${errorPhotoIds.join(', ')}`);
        }

        return { data: updatedPhotos };
      },
    }),
    updateScansWithSma: builder.query({
      queryFn: async ({ projectId, scans }: { projectId?: string; scans: Scan[] }) => {
        if (!projectId) {
          return { data: scans };
        }

        const smaNumbersResults = await Promise.allSettled(
          scans.map((scan) => {
            return request<OrderNumber[]>('GET', {
              path: ['projects', projectId, 'scans', scan.id, 'orderNumbers'],
            });
          }),
        );

        const updatedScans = [...scans];
        const errorScanIds = [] as string[];

        smaNumbersResults.forEach((result, index) => {
          if (result.status === 'rejected') {
            errorScanIds.push(scans[index].id);
          }
          if (result.status === 'fulfilled') {
            updatedScans[index] = { ...updatedScans[index], orderNumbers: result.value };
          }
        });

        if (errorScanIds.length > 0) {
          errorBox(`Failed to load SMA numbers for scans ${errorScanIds.join(', ')}`);
        }

        return { data: updatedScans };
      },
    }),
  }),
});

export const { useUpdatePhotosWithSmaQuery, useUpdateScansWithSmaQuery } = smaApi;
